import { sha256Hex } from "./local-ifc";
import { localBaseName } from "./local-source";

export type LocalExportArtifactKind = "regenerated-ifc" | "verification-receipt" | "original-ifc";

export interface LocalExportArtifact {
  readonly kind: LocalExportArtifactKind;
  readonly filename: string;
  readonly bytes: Uint8Array;
}

export type LocalExportSaveState = "saved" | "cancelled" | "refused-existing" | "failed" | "cleanup-failed";

export interface LocalExportSaveOutcome {
  readonly state: LocalExportSaveState;
  readonly filename: string;
  readonly byteLength?: number;
  readonly sha256?: string;
  readonly reason?: string;
}

interface SaveDialogOptions {
  readonly title: string;
  readonly defaultPath: string;
  readonly filters: { name: string; extensions: string[] }[];
}

export interface NativeLocalExportSaveAdapter {
  save(options: SaveDialogOptions): Promise<unknown>;
  exists(path: string): Promise<boolean>;
  writeFile(path: string, bytes: Uint8Array): Promise<void>;
  readFile(path: string): Promise<Uint8Array>;
  remove(path: string): Promise<void>;
}

const artifactSpecs: Readonly<Record<LocalExportArtifactKind, {
  readonly title: string;
  readonly extension: "ifc" | "json";
  readonly filterName: string;
}>> = {
  "regenerated-ifc": { title: "Save regenerated IFC", extension: "ifc", filterName: "IFC (STEP Physical File)" },
  "verification-receipt": { title: "Save local verification receipt", extension: "json", filterName: "JSON receipt" },
  "original-ifc": { title: "Save original IFC", extension: "ifc", filterName: "IFC (STEP Physical File)" }
};

function defaultFilename(artifact: LocalExportArtifact): string {
  const spec = artifactSpecs[artifact.kind];
  const basename = localBaseName(artifact.filename) || `aec-workbench-export.${spec.extension}`;
  return basename.toLowerCase().endsWith(`.${spec.extension}`) ? basename : `${basename}.${spec.extension}`;
}

function equalBytes(left: Uint8Array, right: Uint8Array): boolean {
  return left.byteLength === right.byteLength && left.every((value, index) => value === right[index]);
}

function failureReason(_error: unknown): string {
  return "Native file operation failed.";
}

async function cleanupFailure(
  adapter: NativeLocalExportSaveAdapter,
  path: string,
  filename: string,
  reason: string
): Promise<LocalExportSaveOutcome> {
  try {
    await adapter.remove(path);
    return { state: "failed", filename, reason };
  } catch {
    return { state: "cleanup-failed", filename, reason: `${reason} Cleanup also failed.` };
  }
}

async function saveOnce(
  adapter: NativeLocalExportSaveAdapter,
  artifact: LocalExportArtifact
): Promise<LocalExportSaveOutcome> {
  const spec = artifactSpecs[artifact.kind];
  const suggestedFilename = defaultFilename(artifact);
  let selected: unknown;
  try {
    selected = await adapter.save({
      title: spec.title,
      defaultPath: suggestedFilename,
      filters: [{ name: spec.filterName, extensions: [spec.extension] }]
    });
  } catch (error) {
    return { state: "failed", filename: suggestedFilename, reason: failureReason(error) };
  }
  if (selected === null || selected === undefined) return { state: "cancelled", filename: suggestedFilename };
  if (typeof selected !== "string" || selected === "") {
    return { state: "failed", filename: suggestedFilename, reason: "Save dialog returned an invalid file target." };
  }

  const filename = localBaseName(selected) || suggestedFilename;
  try {
    if (await adapter.exists(selected)) return { state: "refused-existing", filename };
  } catch (error) {
    return { state: "failed", filename, reason: failureReason(error) };
  }

  const ownedBytes = new Uint8Array(artifact.bytes);
  try {
    await adapter.writeFile(selected, ownedBytes);
    const readBack = await adapter.readFile(selected);
    const [expectedDigest, actualDigest] = await Promise.all([sha256Hex(ownedBytes), sha256Hex(readBack)]);
    if (!equalBytes(ownedBytes, readBack) || expectedDigest !== actualDigest) {
      return cleanupFailure(adapter, selected, filename, "Saved file did not match the requested bytes and digest.");
    }
    return { state: "saved", filename, byteLength: ownedBytes.byteLength, sha256: expectedDigest };
  } catch (error) {
    return cleanupFailure(adapter, selected, filename, failureReason(error));
  }
}

export interface NativeLocalExportSaver {
  save(artifact: LocalExportArtifact): Promise<LocalExportSaveOutcome>;
}

export function createNativeLocalExportSaver(adapter: NativeLocalExportSaveAdapter): NativeLocalExportSaver {
  let active: Promise<LocalExportSaveOutcome> | null = null;
  return {
    save(artifact) {
      if (active !== null) return active;
      active = saveOnce(adapter, artifact).finally(() => {
        active = null;
      });
      return active;
    }
  };
}

let nativeSaverPromise: Promise<NativeLocalExportSaver> | null = null;

export async function saveNativeLocalExport(artifact: LocalExportArtifact): Promise<LocalExportSaveOutcome> {
  if (nativeSaverPromise === null) {
    nativeSaverPromise = Promise.all([
      import("@tauri-apps/plugin-dialog"),
      import("@tauri-apps/plugin-fs")
    ]).then(([{ save }, { exists, writeFile, readFile, remove }]) => (
      createNativeLocalExportSaver({ save, exists, writeFile, readFile, remove })
    )).catch((error: unknown) => {
      nativeSaverPromise = null;
      throw error;
    });
  }
  return (await nativeSaverPromise).save(artifact);
}
